import { useState, useEffect } from 'react';
import { api } from '../../../lib/api';
import { AwayTracker } from '../model/protect';

/**
 * Report to the server when the student leaves the exam screen (tab hidden or app switched).
 * Returns true briefly after they come back, so the screen can remind them it is recorded.
 */
export function useAwaySignal(attemptId: string, enabled: boolean): boolean {
  const [returned, setReturned] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    const away = new AwayTracker();
    let alive = true;
    const send = async () => {
      const batch = away.take();
      if (!batch) return;
      try {
        await api('POST', `/app/grand-mock/attempts/${attemptId}/signal`, batch);
        if (alive && away.pending) void send();
      } catch {
        away.restore(batch);
      }
    };
    const onVis = () => {
      if (document.hidden) away.hide(Date.now());
      else if (away.show(Date.now())) {
        setReturned(true);
        void send();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    // Retry anything that failed to send (for example while offline).
    const retry = setInterval(() => void send(), 30_000);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(retry);
    };
  }, [attemptId, enabled]);
  useEffect(() => {
    if (!returned) return;
    const id = setTimeout(() => setReturned(false), 6000);
    return () => clearTimeout(id);
  }, [returned]);
  return returned;
}
